// src/components/MaterialsViewer.jsx
import React, { useState, useEffect } from 'react';
import categoriasData from '../../databases/camicon/materiales_categorias.json';
import materialesData from '../../databases/camicon/materiales_materiales.json';

// Componente para el dropdown con búsqueda
const SearchableDropdown = ({ 
  label, 
  options, 
  value, 
  onChange, 
  disabled = false,
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <button
        type="button"
        className={`w-full p-2 border ${disabled ? 'bg-gray-100' : 'bg-white'} border-gray-300 rounded text-left flex justify-between items-center ${disabled ? 'text-gray-400' : 'text-gray-700'}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        {value ? options.find(opt => opt.id === value)?.name || value : placeholder}
        <svg 
          className={`h-5 w-5 ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-300">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder={searchPlaceholder}
              className="w-full p-2 border border-gray-300 rounded text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            <button
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 text-sm ${!value ? 'bg-blue-50' : ''}`}
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setSearchTerm('');
              }}
            >
              {placeholder}
            </button>
            
            {filteredOptions.map(option => (
              <button
                key={option.id}
                className={`w-full text-left px-3 py-2 hover:bg-gray-100 text-sm ${value === option.id ? 'bg-blue-50 font-medium' : ''}`}
                onClick={() => {
                  onChange(option.id);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                {option.name}
              </button>
            ))}
            
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No se encontraron resultados
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MaterialsViewer = () => {
  // Estados principales
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [newCurrency, setNewCurrency] = useState('USD');
  const [editMode, setEditMode] = useState(false);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  
  // Estado para nuevo material
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    unit: '',
    supplier: '',
    categoryID: '',
    subcategoryID: '',
    materialID: '',
    uniqueId: Date.now()
  });

  // Estado para los filtros
  const [filters, setFilters] = useState({
    supplier: '',
    category: '',
    subcategory: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    const materialsWithUniqueIds = materialesData.map(material => ({
      ...material,
      uniqueId: material.uniqueId || `${material.id}_${Date.now()}`
    }));
    
    setCategories(categoriasData);
    setMaterials(materialsWithUniqueIds);
    setFilteredMaterials(materialsWithUniqueIds);
  }, []);

  // Actualizar materiales filtrados con búsqueda mejorada
  useEffect(() => 
  {
    const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    
    const filtered = materials.filter(material => 
    {
      // Construir una cadena con todos los campos buscables
      const searchableText = [
        material.name,
        material.categoryName,
        material.subcategoryName,
        material.id,
        material.supplier,
        material.unit
      ].join(' ').toLowerCase();

      // Verificar si todas las palabras de búsqueda están presentes en cualquier orden
      const searchMatch = searchWords.length === 0 || 
        searchWords.every(word => searchableText.includes(word));

      const supplierMatch = !filters.supplier || material.supplier === filters.supplier;
      const categoryMatch = !filters.category || material.categoryID === filters.category;
      const subcategoryMatch = !filters.subcategory || material.subcategoryID === filters.subcategory;
      
      return searchMatch && supplierMatch && categoryMatch && subcategoryMatch;
    });

    setFilteredMaterials(filtered);
  }, [materials, searchTerm, filters]);

  // Obtener lista única de proveedores ordenada alfabéticamente
  const suppliers = [...new Set(materials.map(material => material.supplier).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map(supplier => ({ id: supplier, name: supplier }));

  // Ordenar categorías alfabéticamente
  const sortedCategories = [...categories]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(category => ({ id: category.id, name: category.name }));

  // Obtener subcategorías disponibles ordenadas alfabéticamente
  const availableSubcategories = filters.category 
    ? [...(categories.find(c => c.id === filters.category)?.subcategories || [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(sub => ({ id: sub.id, name: sub.name }))
    : [];

  // Manejar cambio de filtros
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value,
      ...(filterName === 'category' && { subcategory: '' })
    }));
    setSelectedMaterial(null);
  };

  // Seleccionar material
  const handleSelectMaterial = (material) => {
    setSelectedMaterial(material);
    setEditMode(false);
  };

  // Añadir nuevo precio
  const handleAddPrice = () => {
    if (!newPrice || isNaN(newPrice) || !selectedMaterial) return;

    const updatedMaterials = materials.map(material => {
      if (material.uniqueId === selectedMaterial.uniqueId) {
        const newPriceEntry = {
          cost: parseFloat(newPrice),
          currency: newCurrency,
          date: new Date().toISOString(),
          timestamp: Date.now()
        };
        return {
          ...material,
          priceHistory: [...(material.priceHistory || []), newPriceEntry]
        };
      }
      return material;
    });

    setMaterials(updatedMaterials);
    setSelectedMaterial(updatedMaterials.find(m => m.uniqueId === selectedMaterial.uniqueId));
    setNewPrice('');
  };

  // Descargar base de datos
  const handleDownload = () => {
    const data = JSON.stringify({ categories, materials }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'materiales_actualizados.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Crear nuevo material
  const handleCreateMaterial = () => {
    if (!newMaterial.name || !newMaterial.unit || !newMaterial.supplier || 
        !newMaterial.categoryID || !newMaterial.subcategoryID || !newMaterial.materialID) {
      alert('Por favor complete todos los campos');
      return;
    }

    const category = categories.find(c => c.id === newMaterial.categoryID);
    const subcategory = category?.subcategories?.find(s => s.id === newMaterial.subcategoryID);

    const materialId = `${newMaterial.categoryID}.${newMaterial.subcategoryID}.${newMaterial.materialID}`;
    
    const newMaterialEntry = {
      id: materialId,
      uniqueId: `${materialId}_${Date.now()}`,
      name: newMaterial.name,
      unit: newMaterial.unit,
      supplier: newMaterial.supplier,
      priceHistory: [],
      categoryName: category?.name || '',
      categoryID: newMaterial.categoryID,
      subcategoryName: subcategory?.name || '',
      subcategoryID: newMaterial.subcategoryID,
      materialID: newMaterial.materialID
    };

    const updatedMaterials = [...materials, newMaterialEntry];
    setMaterials(updatedMaterials);
    setNewMaterial({
      name: '',
      unit: '',
      supplier: '',
      categoryID: '',
      subcategoryID: '',
      materialID: '',
      uniqueId: Date.now()
    });
    setEditMode(false);
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Visor de Materiales de Construcción</h1>
      
      {/* Barra de búsqueda y filtros */}
      <div className="mb-6 space-y-4">
        <input
          type="text"
          placeholder="Buscar materiales (puede escribir palabras en cualquier orden)..."
          className="w-full p-2 border border-gray-300 rounded"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <SearchableDropdown
              label="Proveedor"
              options={[
                { id: '', name: 'Todos los proveedores' },
                ...suppliers
              ]}
              value={filters.supplier}
              onChange={(value) => handleFilterChange('supplier', value)}
              placeholder="Todos los proveedores"
              searchPlaceholder="Buscar proveedor..."
            />
          </div>
          
          <div>
            <SearchableDropdown
              label="Categoría"
              options={[
                { id: '', name: 'Todas las categorías' },
                ...sortedCategories
              ]}
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
              placeholder="Todas las categorías"
              searchPlaceholder="Buscar categoría..."
            />
          </div>
          
          <div>
            <SearchableDropdown
              label="Subcategoría"
              options={[
                { id: '', name: 'Todas las subcategorías' },
                ...availableSubcategories
              ]}
              value={filters.subcategory}
              onChange={(value) => handleFilterChange('subcategory', value)}
              disabled={!filters.category}
              placeholder={filters.category ? 'Todas las subcategorías' : 'Seleccione categoría primero'}
              searchPlaceholder="Buscar subcategoría..."
            />
          </div>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Lista de materiales */}
        <div className="w-full md:w-1/2">
          <h2 className="text-xl font-semibold mb-4">Materiales ({filteredMaterials.length})</h2>
          <div className="bg-white rounded shadow overflow-hidden">
            {filteredMaterials.length > 0 ? (
              <ul className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                {filteredMaterials.map((material, index) => (
                  <li 
                    key={index}
                    className={`p-3 hover:bg-red-100 cursor-pointer ${selectedMaterial?.uniqueId === material.uniqueId ? 'bg-blue-50' : ''}`}
                    onClick={() => handleSelectMaterial(material)}
                  >
                    <div className="font-medium"> ID: {material.id} | {material.name}</div>
                    <div className="text-sm text-gray-600">
                      {material.categoryName} / {material.subcategoryName}
                    </div>
                    <div className="text-sm">
                      Proveedor: {material.supplier || 'N/A'}
                    </div>
                    <div className="text-sm">
                      {material.priceHistory?.length > 0 ? (
                        <>
                          Precio actual: {material.priceHistory[material.priceHistory.length - 1].cost} {material.priceHistory[material.priceHistory.length - 1].currency}
                        </>
                      ) : (
                        <span className="text-red-500">Sin precios registrados</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-gray-500">No se encontraron materiales</div>
            )}
          </div>
          
          <button
            className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            onClick={() => {
              setSelectedMaterial(null);
              setEditMode(true);
            }}
          >
            Añadir Nuevo Material
          </button>
         
        </div>
        
        {/* Panel de detalles */}
        <div className="w-full md:w-1/2">
          {editMode ? (
            <div className="bg-white p-4 rounded shadow">
              <h2 className="text-xl font-semibold mb-4">
                {selectedMaterial ? 'Editar Material' : 'Nuevo Material'}
              </h2>
              
              {!selectedMaterial ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newMaterial.name}
                      onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Unidad</label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newMaterial.unit}
                      onChange={(e) => setNewMaterial({...newMaterial, unit: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Proveedor</label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newMaterial.supplier}
                      onChange={(e) => setNewMaterial({...newMaterial, supplier: e.target.value})}
                    />
                  </div>
                  <div>
                    <SearchableDropdown
                      label="Categoría"
                      options={sortedCategories}
                      value={newMaterial.categoryID}
                      onChange={(value) => setNewMaterial({...newMaterial, categoryID: value, subcategoryID: ''})}
                      placeholder="Seleccionar categoría"
                      searchPlaceholder="Buscar categoría..."
                    />
                  </div>
                  {newMaterial.categoryID && (
                    <div>
                      <SearchableDropdown
                        label="Subcategoría"
                        options={categories.find(c => c.id === newMaterial.categoryID)?.subcategories?.sort((a, b) => a.name.localeCompare(b.name)).map(sub => ({ id: sub.id, name: sub.name })) || []}
                        value={newMaterial.subcategoryID}
                        onChange={(value) => setNewMaterial({...newMaterial, subcategoryID: value})}
                        placeholder="Seleccionar subcategoría"
                        searchPlaceholder="Buscar subcategoría..."
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID de Material</label>
                    <input
                      type="text"
                      className="w-full p-2 border border-gray-300 rounded"
                      value={newMaterial.materialID}
                      onChange={(e) => setNewMaterial({...newMaterial, materialID: e.target.value})}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      onClick={handleCreateMaterial}
                    >
                      Guardar
                    </button>
                    <button
                      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                      onClick={() => setEditMode(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p>Editar material aquí...</p>
                </div>
              )}
            </div>
          ) : selectedMaterial ? (
            <div className="bg-white p-4 rounded shadow">
              <h2 className="text-xl font-semibold mb-4">{selectedMaterial.name}</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-medium text-gray-700">Categoría</h3>
                  <p>{selectedMaterial.categoryName || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Subcategoría</h3>
                  <p>{selectedMaterial.subcategoryName || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Unidad</h3>
                  <p>{selectedMaterial.unit || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Proveedor</h3>
                  <p>{selectedMaterial.supplier || 'N/A'}</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">ID Completo</h3>
                  <p>{selectedMaterial.id || 'N/A'}</p>
                </div>
              </div>              
              
              <div className="mb-6">
                <h3 className="font-medium text-gray-700 mb-2">Historial de Precios - Quito</h3>
                {selectedMaterial.priceHistory?.length > 0 ? (
                  <div className="bg-gray-50 p-2 rounded max-h-48 overflow-y-auto">
                    {selectedMaterial.priceHistory.map((price, index) => (
                      <div key={`price_${selectedMaterial.uniqueId}_${index}`} className="flex justify-between py-1 border-b border-gray-200 last:border-0">
                        <span>{new Date(price.date).toLocaleDateString()}</span>
                        <span className="font-medium">{price.cost} {price.currency} SIN IVA</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No hay historial de precios</p>
                )}
              </div>

              
                <div className="mb-4">
                  <h3 className="font-medium text-gray-700 mb-2">Añadir Nuevo Precio</h3>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Nuevo precio"
                      className="flex-1 p-2 border border-gray-300 rounded"
                      value={newPrice}
                      onChange={(e) => setNewPrice(e.target.value)}
                    />
                    <select
                      className="p-2 border border-gray-300 rounded"
                      value={newCurrency}
                      onChange={(e) => setNewCurrency(e.target.value)}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                    <button
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      onClick={handleAddPrice}
                    >
                      Añadir
                    </button>
                  </div>
                </div>

                {/* 
                
                <div className="flex justify-between">
                  <button
                    className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                    onClick={() => setEditMode(true)}
                  >
                    Editar Material
                  </button>
                </div>
              */}
            </div>
          ) : (
            <div className="bg-white p-4 rounded shadow flex items-center justify-center h-64">
              <p className="text-gray-500">Seleccione un material para ver los detalles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialsViewer;